import { getDefaultSports } from '../utils/constants';
import { decodeAccessToken, isAccessTokenExpired } from '../utils/accessToken';

const normalizeSportKey = (sportKey) => {
    const normalized = String(sportKey || '').toLowerCase();
    return normalized === 'ncaabb' ? 'ncaab' : normalized;
};

class SessionService {
    static _instance = null;

    constructor() {
        if (SessionService._instance) {
            return SessionService._instance;
        }

        try {
            const statpyderSession = localStorage.getItem('statpyderSession');
            if (statpyderSession) {
                this.session = JSON.parse(statpyderSession);
            } else {
                this.session = this.buildSession();
                localStorage.setItem('statpyderSession', JSON.stringify(this.session));
            }
        } catch (error) {
            console.error('Error parsing statpyderSession', error);
            this.session = this.buildSession();
        }

        SessionService._instance = this;
    }

    buildSession() {
        return {
            'sessionId': null,
            'accessToken': null,
            'userId': null,
            'userSettings': null,
            'paymentSubscription': null,
            'prevView': null,
            'viewState': {},
            'localSettings': {} // Local-only settings that persist across sessions
        }
    }

    static getInstance() {
        return !!SessionService._instance ? SessionService._instance : (SessionService._instance = new SessionService());
    }

    saveSession(sessionId) {
        this.session.sessionId = sessionId;
        this.persistSession();
    }

    saveAccessToken(accessToken) {
        this.session.accessToken = accessToken || null;
        const claims = decodeAccessToken(accessToken);
        this.session.userId = claims?.sub || null;
        this.session.accessClaims = claims;
        this.persistSession();
    }

    applyAuthResults(results = {}) {
        if (results.sessionToken) {
            this.session.sessionId = results.sessionToken;
        }

        if (results.accessToken) {
            this.session.accessToken = results.accessToken;
            const claims = decodeAccessToken(results.accessToken);
            this.session.userId = claims?.sub || null;
            this.session.accessClaims = claims;
        }

        if (results.userSettings) {
            this.session.userSettings = results.userSettings;
        }

        this.session.isAuthenticated = Boolean(
            this.session.sessionId &&
            this.session.accessToken &&
            this.session.userId &&
            !isAccessTokenExpired(this.session.accessClaims)
        );
        this.persistSession();
    }

    setUser(user) {
        if (!user) {
            return;
        }

        this.session.user = user;
        this.session.userId = user.id;
        this.persistSession();
    }

    getUser() {
        return this.session.user;
    }

    getUserId() {
        return this.session.userId;
    }

    getSessionId() {
        return this.session.sessionId;
    }

    hasSessionToken() {
        return !!this.session.sessionId;
    }

    getAccessToken() {
        return this.session.accessToken;
    }

    setIsAuthenticated(isAuthenticated) {
        this.session.isAuthenticated = isAuthenticated;
        this.persistSession();
    }

    setUserSettings(userSettings) {
        this.session.userSettings = userSettings;
        this.persistSession();
    }

    setPaymentSubscription(paymentSubscription) {
        this.session.paymentSubscription = paymentSubscription;
        this.persistSession();
    }

    setViewState(viewName, viewStateData) {
        const expireTime = new Date(Date.now() + 30 * 60 * 1000).toISOString();
        this.session.viewState[viewName] = {...viewStateData, 'expireTime': expireTime};
        this.persistSession();
    }

    getViewState(view) {
        return this.session.viewState[view];
    }

    setPrevView(prevView) {
        this.session.prevView = prevView;
        this.persistSession();
    }

    getPrevView() {
        return this.session.prevView;
    }
    

    isAuthenticated() {
        const claims = this.session.accessClaims || decodeAccessToken(this.session.accessToken);
        return Boolean(
            this.session.sessionId &&
            this.session.accessToken &&
            this.session.userId &&
            !isAccessTokenExpired(claims)
        );
    }

    clearSession() {
        this.session = this.buildSession();
        this.persistSession();
    }

    persistSession() {
        localStorage.setItem('statpyderSession', JSON.stringify(this.session));
    }

    getSession() {
        return this.session;
    }

    getUserSettings() {
        return this.session.userSettings;
    }

    getPaymentSubscription() {
        return this.session.paymentSubscription;
    }

    getUserSports() {
        // Get the user's selected sports from userSettings.sports (a dict of {sport: true/false})
        // Return an array of sport keys where the value is true
        const userSettings = this.getUserSettings();
        const userSettingsSports = userSettings?.sports;
        if (userSettingsSports && Object.keys(userSettingsSports).length > 0) {
            // Object.keys returns all sport names, filter to only those set to true
            return [...new Set(
                Object.keys(userSettingsSports)
                    .filter(sport => userSettingsSports[sport] === true)
                    .map(normalizeSportKey)
            )];
        }
        return getDefaultSports();
    }

    /**
     * Get a local setting value (persists across sessions, no expiration)
     * @param {string} key - Setting key
     * @param {*} defaultValue - Default value if setting doesn't exist
     * @returns {*} The setting value or defaultValue
     */
    getLocalSetting(key, defaultValue = null) {
        // Ensure localSettings exists (for backward compatibility with old sessions)
        if (!this.session.localSettings) {
            this.session.localSettings = {};
        }
        return this.session.localSettings[key] !== undefined ? this.session.localSettings[key] : defaultValue;
    }

    /**
     * Set a local setting value (persists across sessions, no expiration)
     * @param {string} key - Setting key
     * @param {*} value - Setting value
     */
    setLocalSetting(key, value) {
        // Ensure localSettings exists (for backward compatibility with old sessions)
        if (!this.session.localSettings) {
            this.session.localSettings = {};
        }
        this.session.localSettings[key] = value;
        this.persistSession();
    }

    /**
     * Get all local settings
     * @returns {Object} All local settings
     */
    getAllLocalSettings() {
        // Ensure localSettings exists (for backward compatibility with old sessions)
        if (!this.session.localSettings) {
            this.session.localSettings = {};
        }
        return this.session.localSettings;
    }

    /**
     * Set active sports from API response
     * Caches the active sports with a 1-hour expiry
     * @param {Array} activeSports - Array of active sport objects from API
     */
    setActiveSports(activeSports) {
        const expireTime = new Date(Date.now() + 60 * 60 * 1000).toISOString(); // 1 hour from now
        this.session.activeSports = {
            sports: activeSports,
            expireTime: expireTime
        };
        this.persistSession();
    }

    /**
     * Get cached active sports if they exist and are not expired
     * @returns {Array|null} Array of active sport objects or null if expired/missing
     */
    getActiveSports() {
        if (!this.session.activeSports) {
            return null;
        }

        const { sports, expireTime } = this.session.activeSports;
        const now = new Date();
        const expiry = new Date(expireTime);

        // Check if expired
        if (now > expiry) {
            // Clear expired data
            this.session.activeSports = null;
            this.persistSession();
            return null;
        }

        return sports;
    }

    /**
     * Check if active sports cache is expired or missing
     * @returns {boolean} True if expired or missing, false if valid
     */
    isActiveSportsExpired() {
        if (!this.session.activeSports) {
            return true;
        }

        const { expireTime } = this.session.activeSports;
        const now = new Date();
        const expiry = new Date(expireTime);

        return now > expiry;
    }

    /**
     * Get filtered active sports - intersection of active sports (from API) and user's enabled sports
     * Returns sport keys (lowercase) that are both active and enabled by user
     * @returns {Array} Array of sport keys (lowercase strings)
     */
    getFilteredActiveSports() {
        const activeSports = this.getActiveSports();
        const userSports = this.getUserSports(); // Array of sport keys where userSettings.sports[sport] === true

        if (!activeSports || activeSports.length === 0) {
            // If no active sports cached, return user sports as fallback
            return userSports;
        }

        // Convert active sports array to lowercase sport keys for comparison
        const activeSportKeys = activeSports.map(sport => normalizeSportKey(sport.sport_key));

        // Return intersection: sports that are both active (from API) and enabled by user
        return userSports.filter(sport => activeSportKeys.includes(normalizeSportKey(sport)));
    }
}

export default SessionService;
